import "./App.scss";
import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./features/helpers";

import { checkLinkInvite, checkUserLogged } from "./store/AppSlice";
import { loginPriceApi, verify } from "./store/AuthSlice";
import { setSidebarStatus } from "./store/SidebarSlice";
import { fetchUser } from "./store/UserSlice";

import UserHome from "./pages/user/userHome/home";
import Login from "./pages/access/login/login";
import Navbar from "./components/navbar/navbar";
import WalletsPage from "./pages/user/userWallets/walletsPage";
import WalletsList from "./pages/user/userWalletsList/WalletsList";
import AdminHome from "./pages/admin/home/home";
import AdminSidebar from "./components/sidebar/admin-sidebar";
import AdminListInvoices from "./pages/admin/admin-client-invoices/adminListInvoices";
import Supplier from "./pages/organization/supplier/supplier";
import Quotations from "./pages/admin/quotations/quotations";
import ComplianceHome from "./pages/compliance/compliance-home/complianceHome";
import Balance from "./pages/admin/balance/balance";
import AdminListUser from "./pages/admin/admin-list-user/adminListUser";
import Organization from "./pages/admin/organization/organization";
import CompaniesOverview from "./pages/admin/companies-overview/companies-overview";
import Register from "./pages/access/register/register";
import ClientSidebar from "./components/sidebar/sidebar";
import UserProfile from "./pages/user/userProfile/userProfile";
import UserAccounts from "./pages/user/userAccounts/userAccounts";
import { fetchAssets, fetchBanks } from "./store/AssetsSlice";
import Toast from "./components/toast/toast";

import Clients from              "./pages/organization/clients/clients";
import ClientDeposits from       "./pages/organization/organization-client-deposits/organization-client-deposits";
import OrganizationHome from     "./pages/organization/home/organization-home";
import Statement from            "./pages/organization/statement/statement";
import ListPurchases from        "./pages/organization/purchases/listPurchases";
import AdminAssets from          "./pages/organization/assets/assets";
import Credits from              "./pages/organization/credits/credits";
import Profit from               "./pages/organization/profit/profit";
import Receipts from             "./pages/organization/receipts/receipts";
import Repasses from             "./pages/organization/repasses/repasses";
import Envios from               "./pages/organization/envios/envios";
import DepositAccounts from      "./pages/organization/deposit-accounts/deposit-accounts";
import SupplierHome from         "./pages/supplier/supplierHome/supplier-home";

import ProtectedRoute from "./auth/ProtectedRoute";
import { useRole } from "./auth/useRole";
import { AppRole, RouteKey } from "./auth/permissions";

const RootHome: React.FC = () => {
    const { role } = useRole();
    switch (role) {
        case "super-admin": return <AdminHome />;
        case "owner":       return <OrganizationHome />;
        case "compliance":  return <ComplianceHome />;
        case "finance":     return <AdminHome />;
        case "client":      return <UserHome />;
        case "supplier":    return <SupplierHome />;
        case "depositor":   return <UserHome />;
        default:            return <Navigate to="/" replace />;
    }
};


interface AppRoute {
    paths: string[];
    element: React.ReactElement;
    requireKey: RouteKey;
}

const AUTH_ROUTES: AppRoute[] = [
    // Organization / owner area
    { paths: ["/clients"],                         element: <Clients />,           requireKey: "clients" },
    { paths: ["/clients/deposits/:id", "/clientes/depositos/:id"],
                                                   element: <ClientDeposits />,    requireKey: "client-deposits" },
    { paths: ["/purchases", "/clientes/compras"],  element: <ListPurchases />,     requireKey: "purchases" },
    { paths: ["/clients/invoices", "/clientes/envios"],
                                                   element: <AdminListInvoices />, requireKey: "client-invoices" },
    { paths: ["/suppliers", "/fornecedores"],      element: <Supplier />,          requireKey: "suppliers" },
    { paths: ["/profit", "/lucro"],                element: <Profit />,            requireKey: "profit" },
    { paths: ["/quotations", "/cotacao"],          element: <Quotations />,        requireKey: "quotations" },
    { paths: ["/kyt", "/compliance"],              element: <ComplianceHome />,    requireKey: "compliance" },
    { paths: ["/balance", "/saldo"],               element: <Balance />,           requireKey: "balance" },
    { paths: ["/extrato"],                         element: <Statement />,         requireKey: "statement" },
    { paths: ["/receipts"],                        element: <Receipts />,          requireKey: "receipts" },
    { paths: ["/nota-fiscal"],                     element: <Receipts />,          requireKey: "receipts" },
    { paths: ["/repasses"],                        element: <Repasses />,          requireKey: "repasses" },
    { paths: ["/envios"],                          element: <Envios />,            requireKey: "envios" },
    { paths: ["/assets", "/tokens"],               element: <AdminAssets />,       requireKey: "assets" },
    { paths: ["/credits", "/creditos"],            element: <Credits />,           requireKey: "credits" },
    { paths: ["/deposit-accounts", "/contas-deposito"],
                                                   element: <DepositAccounts />,   requireKey: "deposit-accounts" },

    // Super-admin only
    { paths: ["/users", "/usuarios"],              element: <AdminListUser />,     requireKey: "admin-users" },
    { paths: ["/organizations", "/organizacoes"],  element: <Organization />,      requireKey: "admin-organizations" },
    { paths: ["/companies-overview", "/empresas"], element: <CompaniesOverview />, requireKey: "admin-companies" },

    // Client only
    { paths: ["/wallets", "/carteiras"],           element: <WalletsPage />,       requireKey: "wallets" },
    { paths: ["/accounts", "/contas"],             element: <UserAccounts />,      requireKey: "user-accounts" },
    { paths: ["/depositos"],                       element: <ClientDeposits />,    requireKey: "client-deposits-self" },
    { paths: ["/perfil"],                          element: <UserProfile />,       requireKey: "profile" },
    { paths: ["/listar-carteiras"],                element: <WalletsList />,       requireKey: "listar-carteiras" },
];

const RoleSidebar: React.FC = () => {
    const { role } = useRole();
    if (role === "client" || role === "supplier" || role === "depositor") {
        return <ClientSidebar />;
    }
    return <AdminSidebar />;
};

const App: React.FC = () => {

    const dispatch = useAppDispatch();

    const loggedUser     = useAppSelector((state) => state.user.userInfo);
    const loggedUserType = useAppSelector((state) => state.auth.type);
    const statusBar      = useAppSelector((state) => state.sidebar.sidebarStatus);

    const { role } = useRole();

    useEffect(() => {
        // checkUserLogged restaura a sessao do sessionStorage — uma copia
        // congelada no momento do login. Logo depois revalidamos no servidor,
        // senao mudancas feitas por fora (vinculo a empresa, taxa, status) so
        // apareceriam no proximo login.
        dispatch(checkUserLogged()).then((logado) => {
            if (logado) dispatch(fetchUser());
        });
        dispatch(loginPriceApi());
    }, []);

    useEffect(() => {
        const url = window.location.pathname;
        if (url.includes("/join/") && loggedUser?.id) {
            dispatch(checkLinkInvite(url)).then(() => {
                // limpa a URL pra um refresh nao reprocessar o convite
                window.history.replaceState(null, "", "/");
            });
        }
    }, [dispatch, loggedUser?.id]);

    useEffect(() => {
        if (loggedUserType) {
            dispatch(verify());
            if (loggedUserType !== "Super Admin") {
                dispatch(fetchBanks());
                dispatch(fetchAssets());
            }
        }
    }, [loggedUserType]);

    // ---------------- nao logado ----------------
    if (!loggedUserType || !loggedUser) {
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="/"         element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="*"         element={<Login />} />
                </Routes>
                <Toast />
            </BrowserRouter>
        );
    }

    // ---------------- role nao reconhecida — desloga ----------------
    if (!role) {
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="*" element={<Login />} />
                </Routes>
                <Toast />
            </BrowserRouter>
        );
    }

    // ---------------- logado ----------------
    return (
        <BrowserRouter>
            <div className={`wrapper-app ${statusBar ? "active" : ""}`}>
                <Navbar />
                <RoleSidebar />

                {/* Backdrop do menu. So aparece no mobile (o CSS o mantem
                    `display: none` acima de 760px, onde o menu expandido e o
                    estado normal e nao deve escurecer nada). Tocar nele recolhe
                    o menu — e o "clicar fora". */}
                {statusBar && (
                    <div
                        className="sidebar-backdrop"
                        onClick={() => dispatch(setSidebarStatus(false))}
                        aria-hidden="true"
                    />
                )}

                <Routes>

                    <Route path="/" element={<RootHome />} />

                    {AUTH_ROUTES.flatMap((r) =>
                        r.paths.map((p) => (
                            <Route
                                key={p}
                                path={p}
                                element={
                                    <ProtectedRoute requireKey={r.requireKey}>
                                        {r.element}
                                    </ProtectedRoute>
                                }
                            />
                        ))
                    )}

                    <Route path="*" element={<RootHome />} />
                </Routes>
            </div>
            <Toast />
        </BrowserRouter>
    );
};

export default App;

// Mantido para compat com codigo que pode importar:
export enum UserType {
    SuperAdmin = "Super Admin",
    Owner      = "Owner",
    Compliance = "compliance",
}

export type { AppRole };
